import { FaqEntry } from "@web-src/modules/common/components/FaqEntry";
import { Button, DialogWithHero } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { FC, useMemo, useState } from "react";
import { ReactComponent as GemIcon } from "./assets/gem.svg";
import { ReactComponent as TicketIcon } from "./assets/ticket.svg";

interface OutOfTicketsDialogProps {
  isOpen: boolean;
  usedTickets: number;
  totalTickets: number;
  onRequestClose: () => void;
}

export const OutOfTicketsDialog: FC<OutOfTicketsDialogProps> = ({
  isOpen,
  usedTickets,
  totalTickets,
  onRequestClose,
}) => {
  const { t } = useTranslations();
  const { navigateToPage } = useNavigation();
  const [openId, setOpenId] = useState<string | null>(null);

  const questions = useMemo(
    () => [
      {
        id: "how-usage-calculated",
        title: t({
          id: "client-support-page.tickets-faq-usage-question",
          defaultMessage: "How the usage is calculated?",
        }),
        content: t({
          id: "client-support-page.tickets-faq-usage-answer",
          defaultMessage:
            "Your usage reflects the total number of tickets in your workspace, including resolved ones. Once you reach your plan's limit you'll need to upgrade your plan to create more tickets.",
        }),
      },
      {
        id: "more-ticket-credits",
        title: t({
          id: "client-support-page.tickets-faq-credits-question",
          defaultMessage: "Can I get more ticket credits?",
        }),
        content: t({
          id: "client-support-page.tickets-faq-credits-answer",
          defaultMessage:
            "Yes. Upgrade your Jugl subscription to a higher plan to increase the number of tickets your workspace can have.",
        }),
      },
      {
        id: "modify-subscription-plan",
        title: t({
          id: "client-support-page.tickets-faq-modify-question",
          defaultMessage: "Can I modify my Jugl subscription plan?",
        }),
        content: t({
          id: "client-support-page.tickets-faq-modify-answer",
          defaultMessage:
            "Absolutely. You can upgrade, downgrade, or cancel your Jugl subscription at any time. Any applicable prorated charges or refunds will be automatically calculated based on your current plan and the exact date you make the change.",
        }),
      },
    ],
    [t]
  );

  return (
    <DialogWithHero
      isOpen={isOpen}
      onClose={onRequestClose}
      upperTitle={
        <span className="font-secondary text-[22px] leading-[33px] font-light text-grey-200">
          {t({
            id: "client-support-page.out-of-tickets",
            defaultMessage: "Out of Tickets 🚨",
          })}
        </span>
      }
      title={
        <>
          <TicketIcon />
          <span className="font-secondary text-[40px] leading-[60px] font-semibold text-white">
            {t(
              {
                id: "client-support-page.tickets-used-amount",
                defaultMessage: "{used} of {total} used",
              },
              { used: usedTickets, total: totalTickets }
            )}
          </span>
        </>
      }
      description={t({
        id: "client-support-page.tickets-upgrade-prompt",
        defaultMessage: "Upgrade your plan to get more Ticket Credits",
      })}
      headerChildren={
        <Button
          className="mt-[34px] h-12 w-[300px]"
          iconEnd={<GemIcon />}
          onClick={() => navigateToPage("cxWorkspacePlans")}
        >
          {t({
            id: "client-support-page.upgrade-plan",
            defaultMessage: "Upgrade Plan",
          })}
        </Button>
      }
    >
      <div className="flex flex-col items-center justify-center gap-5 px-[26px] py-7">
        <div className="font-secondary text-xl leading-[30px] font-semibold text-black">
          {t({
            id: "client-support-page.got-questions",
            defaultMessage: "Got questions? We have answers! 🙌",
          })}
        </div>
        <div className="flex w-full flex-col gap-2">
          {questions.map((question) => (
            <FaqEntry
              key={question.id}
              question={question.title}
              answer={question.content}
              isOpen={openId === question.id}
              onOpenSwitch={() =>
                setOpenId((prev) => (prev === question.id ? null : question.id))
              }
            />
          ))}
        </div>
      </div>
    </DialogWithHero>
  );
};
